import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { DeleteOrderAdjustmentPayload, ReconciliationSupplierOrder } from './types';

const getEndpoint = (orderId: number) => `ums/v1/cms/reconciliation/orders/${orderId}/adjustment`;

type TData = ReconciliationSupplierOrder;
type TError = AxiosError;
type TVariables = DeleteOrderAdjustmentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Drops the overlay — the order reconciles at its platform figures again. 409 when closed. */
export function useDeleteOrderAdjustmentApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ orderId }: TVariables) => {
    try {
      const { data } = await http.delete(getEndpoint(orderId));
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status) throw error;

      toast({
        description: error.response?.data?.message ?? 'Failed to reset the order adjustment',
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-delete-order-adjustment'] });
}
